import React from "react";
import { fmtNum, fmtPct } from "./Primitives";

const ACCENT = "var(--accent)";
const SERIES = ["#8d76ff", "#62b47a", "#d6a03c", "#5aa9c9", "#c9739d", "#9a978f"];

function fmtDay(s) {
  if (!s) return "";
  const [, m, d] = s.split("-");
  return `${d}/${m}`;
}

export function LineChart({ data = [], height = 170, color = ACCENT }) {
  const W = 600, H = height, pad = { l: 4, r: 4, t: 10, b: 6 };
  const max = Math.max(1, ...data.map((d) => d.count));
  const stepX = data.length > 1 ? (W - pad.l - pad.r) / (data.length - 1) : 0;
  const y = (v) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const x = (i) => (data.length > 1 ? pad.l + stepX * i : W / 2);
  const line = data.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d.count).toFixed(1)}`).join(" ");
  const area = data.length ? `${line} L${x(data.length - 1)},${H - pad.b} L${x(0)},${H - pad.b} Z` : "";
  return (
    <figure className="px-4 pb-3" aria-label={`Série diária, máximo ${max}`}>
      <div className="flex justify-end text-[11px] mono text-[var(--muted2)] mb-1">máx. {fmtNum(max)}</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full block" style={{ height }} preserveAspectRatio="none">
        {[0, 0.5, 1].map((g) => (
          <line key={g} x1={0} x2={W} y1={pad.t + (H - pad.t - pad.b) * g} y2={pad.t + (H - pad.t - pad.b) * g}
            stroke="var(--border)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        ))}
        {area && <path d={area} fill={color} opacity="0.08" />}
        {line && <path d={line} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />}
        {data.length === 1 && <circle cx={x(0)} cy={y(data[0].count)} r="3" fill={color} />}
      </svg>
      <div className="flex justify-between text-[11px] text-[var(--muted2)] mono mt-1">
        {data.length > 0 && <span>{fmtDay(data[0].date)}</span>}
        {data.length > 2 && <span>{fmtDay(data[Math.floor(data.length / 2)].date)}</span>}
        {data.length > 1 && <span>{fmtDay(data[data.length - 1].date)}</span>}
      </div>
    </figure>
  );
}

export function BarList({ items = [], labelKey = "name", valueKey = "count", renderLabel, color = ACCENT }) {
  const max = Math.max(1, ...items.map((i) => i[valueKey] || 0));
  return (
    <ol className="px-4 pb-3">
      {items.map((it, idx) => {
        const v = it[valueKey] || 0;
        return (
          <li key={idx} className="grid grid-cols-[18px_1fr_auto] items-center gap-x-2 py-1.5">
            <span className="mono text-[11px] text-[var(--muted2)]">{idx + 1}</span>
            <div className="min-w-0">
              <span className="block text-[13px] truncate">{renderLabel ? renderLabel(it) : it[labelKey]}</span>
              <span className="block h-[3px] mt-1 rounded-full" style={{ width: `${Math.max((v / max) * 100, 1.5)}%`, background: color, opacity: idx === 0 ? 1 : 0.55 }} />
            </div>
            <span className="mono text-[12px] text-[var(--muted)]">{fmtNum(v)}</span>
          </li>
        );
      })}
    </ol>
  );
}

// Share of total as one segmented bar + legend (replaces the decorative donut)
export function Donut({ items = [] }) {
  const total = items.reduce((a, b) => a + (b.value || 0), 0);
  return (
    <div className="px-4 pb-3">
      <div className="flex h-2 rounded-full overflow-hidden" style={{ background: "var(--surface3)" }}>
        {total > 0 && items.map((it, i) => (
          <span key={i} style={{ width: `${(it.value / total) * 100}%`, background: SERIES[i % SERIES.length] }} title={`${it.label}: ${it.value}`} />
        ))}
      </div>
      <table className="ops-table mt-2">
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td className="!px-0 !py-1.5">
                <span className="inline-block w-2 h-2 rounded-[2px] mr-2 align-middle" style={{ background: SERIES[i % SERIES.length] }} />
                {it.label}
              </td>
              <td className="num !py-1.5 text-[var(--muted)]">{total ? fmtPct((it.value / total) * 100) : "—"}</td>
              <td className="num !pr-0 !py-1.5">{fmtNum(it.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Funnel({ stages = [], unitLabel = "sessões" }) {
  const max = Math.max(1, ...stages.map((s) => s.count ?? s.sessions ?? 0));
  const hasBase = (stages[0]?.count ?? stages[0]?.sessions ?? 0) > 0;
  return (
    <div className="overflow-x-auto mia-scroll">
      <table className="ops-table" data-testid="funnel-table">
        <thead>
          <tr>
            <th className="w-8">#</th><th>Etapa</th><th className="w-[30%] hidden sm:table-cell" />
            <th className="!text-right">{unitLabel}</th><th className="!text-right">% da entrada</th><th className="!text-right">Da etapa anterior</th>
          </tr>
        </thead>
        <tbody>
          {stages.map((s, i) => {
            const c = s.count ?? s.sessions ?? 0;
            return (
              <tr key={s.stage} data-testid={`funnel-stage-${s.stage}`}>
                <td className="mono text-[var(--muted2)]">{i + 1}</td>
                <td className="whitespace-nowrap">{s.label || s.stage}</td>
                <td className="hidden sm:table-cell">
                  <span className="block h-[6px] rounded-[2px]" style={{ width: `${Math.max((c / max) * 100, 1)}%`, background: "var(--accent)", opacity: 0.35 + 0.65 * (c / max) }} />
                </td>
                <td className="num">{fmtNum(c)}</td>
                <td className="num text-[var(--muted)]">{i === 0 || !hasBase ? "—" : fmtPct(s.rate)}</td>
                <td className="num" title={s.prev_count != null ? `${s.step_converted} de ${s.prev_count}` : ""}>
                  {i === 0 ? "—" : s.step_rate == null ? "—" : fmtPct(s.step_rate)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function HourBars({ hours = [] }) {
  const max = Math.max(1, ...hours.map((h) => h.count));
  const peak = hours.reduce((a, b) => (b.count > (a?.count || 0) ? b : a), null);
  return (
    <div className="px-4 pb-3">
      <div className="flex items-end gap-[2px] h-24" role="img" aria-label="Eventos por hora do dia">
        {hours.map((h) => (
          <div key={h.hour} className="flex-1 h-full flex items-end" title={`${String(h.hour).padStart(2, "0")}h — ${h.count}`}>
            <div className="w-full rounded-t-[1px]" style={{ height: `${h.count ? Math.max((h.count / max) * 100, 3) : 1}%`, background: h.count ? (peak && h.hour === peak.hour ? "var(--accent)" : "var(--surface3)") : "var(--border)" }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[11px] text-[var(--muted2)] mono mt-1.5">
        <span>00h</span><span>06h</span><span>12h</span><span>18h</span><span>23h</span>
      </div>
      {peak && peak.count > 0 && <p className="ops-help mt-2">Pico às {String(peak.hour).padStart(2, "0")}h com {fmtNum(peak.count)} eventos.</p>}
    </div>
  );
}
